import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import { Input, message, Table } from "antd";
import { SearchOutlined, ReloadOutlined } from "@ant-design/icons";
import { IsValidRoute, Logout } from "../../../utils/AccountUtils";
import { ComplainceTabapi } from "../Compliances/ComplianceTabapi";
import { getComplianceColumns, FraudScreenDataType } from "./FraudScreenColumns";
import PageWrapper from "../../../Layout/PageWrapper";
import { ProgressSpinner } from "primereact/progressspinner";
import { SearchContainer, SearchWrapper, ButtonGroup, TableContainer, PopupOverlay, PopupContent, PopupButtons } from "./FraudScreen.styles";
import Copy_icon from "../../../assets/images/copy.svg";
import { GlobalButton } from "../../comman/GlobalButton.styles";
import { FraudScreenApi } from "../../../services/FraudScreen/FraudScreenApi";

export type SanctionDetailsType = "sender" | "receiver";

const FraudScreen: React.FC = () => {
	const navigate = useNavigate();
	const toast = useRef<Toast>(null);
	const [loading, setLoading] = useState(false);
	const [pageloading, setPageLoading] = useState(true);
	const [reportData, setReportData] = useState<FraudScreenDataType[]>([]);
	const [totalRows, setTotalRows] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [searchValue, setSearchValue] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");

	// Popup states
	const [isPopupVisible, setPopupVisible] = useState(false);
	const [popupContent, setPopupContent] = useState("");
	const [isConfirmPopupOpen, setIsConfirmPopupOpen] = useState(false);
	const [isRejectPopupOpen1, setIsRejectPopupOpen1] = useState(false);
	const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null);
	const [actioncomment, setActionComment] = useState("");
	const [actioncomment1, setActionComment1] = useState("");
	const [approvePayments, setApprovePayments] = useState<string[]>([]);
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const [sanctionDetailsType, setSanctionDetailsType] = useState<SanctionDetailsType>("sender");

	const complianceExternal1 = sessionStorage.getItem("complianceExternal");
	const partnerID = sessionStorage.getItem("PartnerId");

	useEffect(() => {
		IsValidRoute(navigate);
		const userObj = sessionStorage.getItem("User");
		if (!userObj) {
			Logout(navigate);
			return;
		}
	}, []);

	// Search runs on the server across all pages, so wait for typing to pause and restart from page 1
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(searchValue.trim());
			setCurrentPage(1);
		}, 500);
		return () => clearTimeout(timer);
	}, [searchValue]);

	useEffect(() => {
		getReportData();
	}, [currentPage, pageSize, debouncedSearch]);

	const getReportData = () => {
		setLoading(true);
		FraudScreenApi.getFraudList(partnerID, currentPage, pageSize, debouncedSearch)
			.then((data: any) => {
				setReportData(data.data.fraudTransactionList);
				setTotalRows(data.data.totalRows);
				setLoading(false);
				setPageLoading(false);
			})
			.catch((error: any) => {
				handleError(error);
				setReportData([]);
				setLoading(false);
				setPageLoading(false);
			});
	};

	const handleError = (error: any) => {
		console.log(error);
		if (error.response?.status === 401) {
			toast.current?.show({
				severity: "error",
				summary: "Unauthorized",
				life: 3000
			});
			Logout(navigate);
		} else if (error.response?.status === 400) {
			toast.current?.show({
				severity: "error",
				summary: error.response.data[0].errorMessage,
				life: 3000
			});
		} else {
			toast.current?.show({
				severity: "error",
				summary: error.response.data.message || error.message.data.title,
				life: 3000
			});
		}
	};

	const handleTableChange = (pagination: any) => {
		setCurrentPage(pagination.current);
		setPageSize(pagination.pageSize);
	};

	const handleOpenPopup = (paymentId: string) => {
		setSelectedPaymentId(paymentId);
		setIsConfirmPopupOpen(true);
	};

	const handleRejectPopup1 = (paymentId: string) => {
		setSelectedPaymentId(paymentId);
		setIsRejectPopupOpen1(true);
	};

	const formatSanctionResponse = (jsonString: string) => {
		try {
			const data = JSON.parse(jsonString);
			let formattedText = "Sanctions Check:\n";

			// Format Sanctions Data
			if (data.sanctions && Array.isArray(data.sanctions)) {
				data.sanctions.forEach((sanction: any, index: number) => {
					formattedText += `\n${index + 1}. Status: ${sanction.httpStatus}\n`;
					formattedText += `   Message: ${sanction.message}\n`;
					if (sanction.result && Array.isArray(sanction.result)) {
						formattedText += `   Results: ${sanction.result.length === 0 ? "No results found" : JSON.stringify(sanction.result, null, 3)}\n`;
					}
				});
			}

			// Format PEP Data
			if (data.pep) {
				formattedText += "\nPEP Check:\n";
				formattedText += `Status: ${data.pep.httpStatus}\n`;
				formattedText += `Message: ${data.pep.message}\n`;

				if (data.pep.result && Array.isArray(data.pep.result)) {
					data.pep.result.forEach((pepResult: any, index: number) => {
						formattedText += `\nResult ${index + 1}:\n`;
						formattedText += `ID: ${pepResult.id}\n`;
						formattedText += `Match Count: ${pepResult.matchCount}\n`;
						if (pepResult.matched && Array.isArray(pepResult.matched)) {
							formattedText += `Matched Items: ${pepResult.matched.length === 0 ? "None" : JSON.stringify(pepResult.matched, null, 3)}\n`;
						}
					});
				}
			}

			return formattedText;
		} catch (error) {
			return "Error parsing sanction details";
		}
	};

	const handleSanctionDetailClick = (rowData: FraudScreenDataType) => {
		try {
			// Parse the JSON string
			const jsonData = JSON.parse(
				sanctionDetailsType === "sender" ? rowData.senderSanctiondetailResponse : rowData.receiverSanctiondetailResponse
			);
			// Format it with proper indentation
			const formattedContent = JSON.stringify(jsonData, null, 2);
			// setPopupContent(formatSanctionResponse(formattedContent));
			setPopupContent(formattedContent);
			setPopupVisible(true);
		} catch (error) {
			// If JSON parsing fails, show the original content
			setPopupContent(rowData.senderSanctiondetailResponse);
			setPopupVisible(true);
		}
	};

	const handleNoteClick = (rowData: FraudScreenDataType) => {
		const combinedRemarks = `${rowData.remark1 || ""} \n ${rowData.remark2 || ""}`.trim();
		setPopupContent(combinedRemarks);
		setPopupVisible(true);
	};

	const handleClosePopup = () => {
		if (!selectedPaymentId) return;

		setLoading(true);
		ComplainceTabapi.ApproveReject(selectedPaymentId, "APPROVE", actioncomment)
			.then(() => {
				setApprovePayments((prev) => [...prev, selectedPaymentId]);
				setIsConfirmPopupOpen(false);
				setActionComment("");
				getReportData();
			})
			.catch((error) => {
				handleError(error);
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handleRejectPopup = () => {
		if (!selectedPaymentId) return;

		setLoading(true);
		ComplainceTabapi.ApproveReject(selectedPaymentId, "REJECT", actioncomment1)
			.then(() => {
				setApprovePayments((prev) => [...prev, selectedPaymentId]);
				setIsRejectPopupOpen1(false);
				setActionComment1("");
				getReportData();
			})
			.catch((error) => {
				handleError(error);
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const copyToClipBoard = () => {
		if (textareaRef.current) {
			textareaRef.current.select();
			navigator.clipboard
				.writeText(textareaRef.current.value)
				.then(() => {
					message.success({
						content: "Copied to clipboard!",
						duration: 2,
						style: {
							marginTop: "10px",
							position: "fixed",
							top: "20px",
							left: "50%",
							transform: "translateX(-50%)",
							zIndex: "9999"
						}
					});
				})
				.catch(() => {
					message.error({
						content: "Failed to copy text",
						duration: 2,
						style: {
							marginTop: "10px",
							position: "fixed",
							top: "20px",
							left: "50%",
							transform: "translateX(-50%)",
							zIndex: "9999"
						}
					});
				});
		}
	};

	const headerRight = (
		<ButtonGroup>
			<GlobalButton className="primary" icon={<ReloadOutlined />} onClick={getReportData} loading={loading}>
				Refresh
			</GlobalButton>
		</ButtonGroup>
	);

	if (pageloading) {
		return (
			<div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
				<ProgressSpinner />
			</div>
		);
	}

	return (
		<PageWrapper title="Fraud Screen" headerRight={headerRight}>
			<Toast ref={toast} />
			<SearchContainer>
				<SearchWrapper>
					<Input
						placeholder="Search by Payment ID..."
						value={searchValue}
						onChange={(e) => setSearchValue(e.target.value)}
						prefix={<SearchOutlined />}
						allowClear
					/>
				</SearchWrapper>
			</SearchContainer>

			<TableContainer>
				<Table<FraudScreenDataType>
					columns={getComplianceColumns(
						complianceExternal1,
						handleOpenPopup,
						handleRejectPopup1,
						handleSanctionDetailClick,
						handleNoteClick,
						navigate,
						setSanctionDetailsType
					)}
					dataSource={reportData}
					loading={loading}
					pagination={{
						total: totalRows,
						current: currentPage,
						pageSize: pageSize,
						showSizeChanger: true,
						showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
					}}
					onChange={handleTableChange}
					scroll={{ x: "max-content" }}
					rowKey="id"
				/>
			</TableContainer>

			{/* Sanction Detail Popup */}
			{isPopupVisible && (
				<PopupOverlay>
					<PopupContent>
						<h2>Sanction Detail Response</h2>
						<div style={{ position: "relative" }}>
							<img
								src={Copy_icon}
								alt="Copy"
								onClick={copyToClipBoard}
								style={{
									position: "absolute",
									right: "10px",
									top: "10px",
									cursor: "pointer"
								}}
							/>
							<textarea ref={textareaRef} value={popupContent} readOnly />
						</div>
						<PopupButtons>
							<GlobalButton className="primary" onClick={() => setPopupVisible(false)}>
								Close
							</GlobalButton>
						</PopupButtons>
					</PopupContent>
				</PopupOverlay>
			)}

			{/* Approve Confirmation Popup */}
			{isConfirmPopupOpen && (
				<PopupOverlay>
					<PopupContent>
						<h2>Confirm Approval</h2>
						<p>Are you sure you want to approve this payment?</p>
						<textarea value={actioncomment} onChange={(e) => setActionComment(e.target.value)} placeholder="Enter your notes here" />
						<PopupButtons>
							<GlobalButton className="primary" onClick={handleClosePopup} disabled={!actioncomment.trim()} loading={loading}>
								Yes
							</GlobalButton>
							<GlobalButton
								className="secondary"
								onClick={() => {
									setIsConfirmPopupOpen(false);
									setActionComment("");
								}}
							>
								No
							</GlobalButton>
						</PopupButtons>
					</PopupContent>
				</PopupOverlay>
			)}

			{/* Reject Confirmation Popup */}
			{isRejectPopupOpen1 && (
				<PopupOverlay>
					<PopupContent>
						<h2>Confirm Rejection</h2>
						<p>Are you sure you want to reject this payment?</p>
						<textarea value={actioncomment1} onChange={(e) => setActionComment1(e.target.value)} placeholder="Enter your notes here" />
						<PopupButtons>
							<GlobalButton className="primary" onClick={handleRejectPopup} disabled={!actioncomment1.trim()} loading={loading}>
								Yes
							</GlobalButton>
							<GlobalButton
								className="secondary"
								onClick={() => {
									setIsRejectPopupOpen1(false);
									setActionComment1("");
								}}
							>
								No
							</GlobalButton>
						</PopupButtons>
					</PopupContent>
				</PopupOverlay>
			)}
		</PageWrapper>
	);
};

export default FraudScreen;
